import React, { useEffect, useRef, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../../context/AuthContext';
import FeedbackForm from './FeedbackForm';
import feedbackService from '../../../services/feedbackService';

/**
 * FeedbackModal component - Accessible modal for user feedback submission
 * Features focus management, keyboard navigation, and mobile-first design
 */
const FeedbackModal = ({ isOpen, onClose }) => {
  const { user, userProfile } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState(null);

  // Refs for focus management
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Store the previously focused element when modal opens
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      
      // Prevent body scroll on mobile
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      
      // Focus on close button after modal animation
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 150);
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      
      // Restore focus to previously focused element
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
      
      // Reset modal state when closed
      setIsSubmitting(false);
      setSubmitError(null);
      setIsSuccess(false);
      setSubmittedFeedback(null);
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isSubmitting]);

  // Focus trap implementation
  useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await feedbackService.submitFeedback(formData, user, userProfile);
      
      if (result.success) {
        setSubmittedFeedback(formData);
        setIsSuccess(true);
        
        // Auto-close after success delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Create the modal content
  const modalContent = (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      aria-describedby="feedback-modal-description"
    >
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
          <div 
            ref={modalRef}
            className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all relative mx-2 sm:mx-0 max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 id="feedback-modal-title" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Share Your Feedback
            </h2>
            <p id="feedback-modal-description" className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Help us improve by reporting bugs or suggesting enhancements
            </p>
          </div>

          <button
            ref={closeButtonRef}
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                     hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors
                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
                     disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close feedback modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(100vh-8rem)] sm:max-h-[calc(100vh-6rem)] pb-20 sm:pb-6">
            {isSuccess ? (
              /* Success State */
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Thank You for Your Feedback!
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your {submittedFeedback?.type === 'bug' ? 'bug report' : 'suggestion'} has been submitted successfully.
                  Our team will review it and get back to you if needed.
                </p>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-left">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {submittedFeedback?.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {submittedFeedback?.description}
                  </p>
                </div>
                
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  This modal will close automatically in a moment...
                </p>
              </div>
            ) : (
              /* Form State */
              <FeedbackForm
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                error={submitError}
              />
            )}
        </div>

        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-80 dark:bg-opacity-80 
                         flex items-center justify-center backdrop-blur-sm">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-900 dark:text-white font-medium">Submitting your feedback...</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Please wait a moment</p>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root
  return createPortal(modalContent, document.body);
};

export default FeedbackModal;